import { useState } from "react";
import { Download, X } from "lucide-react";
import FftBarGraph from "components/project/FftBarGraph";
import {
  getFusionResultLabel,
  getFusionResultTone,
} from "lib/utils/fusionResult";
import { useAppStore } from "lib/store/useAppStore";
import { useTranslation } from "react-i18next";

const EMPTY_VALUE = "-";

export function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function displayValue(value, unit = "") {
  if (!hasValue(value)) return EMPTY_VALUE;
  return `${value}${unit}`;
}

function parseNumber(value) {
  const numericValue = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getThreePhaseTemperatureDiff(file, t) {
  const values = getThermalClickPointNumbers(file);

  if (values.length < 3) {
    return t("fusionPanel.status.threePhaseRequired", {
      defaultValue: "3상 비교 분석 필요",
    });
  }

  return `${(Math.max(...values) - Math.min(...values)).toFixed(1)} °C`;
}

function getThermalClickPointNumbers(file) {
  const points = Array.isArray(file?.click_points) ? file.click_points : [];
  const values = [0, 1, 2].map((index) => parseNumber(points[index]?.value));
  const hasCompleteValues = values.every((value) => value !== null);
  const isDefaultZeroSet =
    hasCompleteValues && values.every((value) => value === 0);

  if (!hasCompleteValues || isDefaultZeroSet) return [];

  return values;
}

function formatClickPointValue(point) {
  const rawValue = point?.value;
  const numericValue = parseNumber(rawValue);
  if (numericValue === null) return EMPTY_VALUE;

  const textValue = String(rawValue ?? "");
  if (textValue.includes("°") || textValue.includes("℃")) {
    return textValue;
  }

  return `${numericValue} °C`;
}

function getThermalClickPointValues(file) {
  const points = Array.isArray(file?.click_points) ? file.click_points : [];
  const values = getThermalClickPointNumbers(file);

  if (values.length < 3) return [];

  return [0, 1, 2].map((index) => formatClickPointValue(points[index]));
}

function getMetricRows(file, t, fusionLabel) {
  return [
    {
      group: t("fusionPanel.groups.fusion", { defaultValue: "융복합" }),
      label: t("imageFileIndex.report.fusionResult", {
        defaultValue: "융복합 진단 결과",
      }),
      value: fusionLabel,
    },
    {
      group: t("fusionPanel.groups.ultrasonic", { defaultValue: "초음파" }),
      label: t("fileValueTable.ultrasonic.detected", { defaultValue: "검출" }),
      value: displayValue(file?.avg_decibel ?? file?.decibel, " dB"),
    },
    {
      group: t("fusionPanel.groups.ultrasonic", { defaultValue: "초음파" }),
      label: t("fileValueTable.ultrasonic.max", { defaultValue: "최대" }),
      value: displayValue(file?.max_decibel, " dB"),
    },
    {
      group: t("fusionPanel.groups.ultrasonic", { defaultValue: "초음파" }),
      label: t("fileValueTable.ultrasonic.peakFrequency", {
        defaultValue: "최대 주파수",
      }),
      value: displayValue(file?.peak_hz, " kHz"),
    },
    {
      group: t("fusionPanel.groups.ultrasonic", { defaultValue: "초음파" }),
      label: t("fusionPanel.metrics.volume", { defaultValue: "설정 볼륨" }),
      value: displayValue(file?.volume),
    },
    {
      group: t("fusionPanel.groups.thermal", { defaultValue: "열화상" }),
      label: t("fileValueTable.thermal.detected", { defaultValue: "검출" }),
      value: displayValue(file?.thermal_center, " °C"),
    },
    {
      group: t("fusionPanel.groups.thermal", { defaultValue: "열화상" }),
      label: t("fileValueTable.thermal.max", { defaultValue: "최고" }),
      value: displayValue(file?.thermal_max, " °C"),
    },
    {
      group: t("fusionPanel.groups.thermal", { defaultValue: "열화상" }),
      label: t("fileValueTable.thermal.min", { defaultValue: "최저" }),
      value: displayValue(file?.thermal_min, " °C"),
    },
    {
      group: t("fusionPanel.groups.thermal", { defaultValue: "열화상" }),
      label: t("fileValueTable.thermal.avg", { defaultValue: "평균" }),
      value: displayValue(file?.thermal_avg, " °C"),
    },
    {
      group: t("fusionPanel.groups.ultraviolet", { defaultValue: "자외선" }),
      label: t("fileValueTable.ultraviolet.detected", {
        defaultValue: "검출 여부",
      }),
      value: displayValue(file?.uv_detected),
    },
    {
      group: t("fusionPanel.groups.ultraviolet", { defaultValue: "자외선" }),
      label: t("fileValueTable.ultraviolet.particleCount", {
        defaultValue: "파티클 개수",
      }),
      value: displayValue(
        file?.particle_count,
        t("fusionPanel.units.count", { defaultValue: " 개" }),
      ),
    },
    {
      group: t("fusionPanel.groups.ultraviolet", { defaultValue: "자외선" }),
      label: t("fileValueTable.ultraviolet.maxParticleArea", {
        defaultValue: "최대 파티클 면적",
      }),
      value: displayValue(file?.max_particle_area, " px²"),
    },
  ];
}

function getMetricGroups(rows) {
  const groupKeys = ["ultrasonic", "thermal", "ultraviolet"];

  return rows.slice(1).reduce((groups, row) => {
    const group = groups.find((item) => item.title === row.group);
    if (group) {
      group.rows.push(row);
    } else {
      groups.push({
        key: groupKeys[groups.length] ?? row.group,
        title: row.group,
        rows: [row],
      });
    }
    return groups;
  }, []);
}

function getSlideImages(file, t) {
  return [
    {
      key: "visual",
      label: t("fusionPanel.images.visual", { defaultValue: "초음파 이미지" }),
      src: file?.image_visual || file?.markup_image_visual,
      analysisKey: "visual",
      downloadKey: "image_visual",
    },
    {
      key: "thermal",
      label: t("fusionPanel.images.thermal", { defaultValue: "열화상 이미지" }),
      src: file?.markup_image_thermal || file?.image_thermal,
      analysisKey: "thermal",
      downloadKey: "markup_image_thermal",
    },
    {
      key: "triplespot",
      label: t("imageFileIndex.subTitle.tripleSpot", {
        defaultValue: "* 3상 비교",
      }).replace(/^\*\s*/, ""),
      src: file?.markup_image_triplespot,
      optional: true,
      analysisKey: "triplespot",
      downloadKey: "markup_image_triplespot",
    },
    {
      key: "uv",
      label: t("fusionPanel.images.uv", { defaultValue: "자외선 이미지" }),
      src: file?.image_uv || file?.markup_image_uv,
      analysisKey: "uv",
      downloadKey: "image_uv",
    },
  ].filter((image) => hasValue(image.src) || !image.optional);
}

function getSpectrogramImage(file, t) {
  return {
    key: "spectrogram",
    label: t("imageFileIndex.subTitle.spectrogram", {
      defaultValue: "* 스펙트로그램",
    }).replace(/^\*\s*/, ""),
    src: file?.image_spectrogram,
    analysisKey: "spectrogram",
    downloadKey: "image_spectrogram",
  };
}

function getFftGraphImage(file, t) {
  return {
    key: "graph",
    label: t("fusionPanel.sections.fftGraph", {
      defaultValue: "FFT 그래프",
    }),
    src: file?.markup_image_graph,
    analysisKey: "graph",
    downloadKey: "markup_image_graph",
  };
}

function getImageAnalysisRows(imageKey, file, t) {
  const thermalClickPointValues = getThermalClickPointValues(file);
  const threePhaseTemperatureDiff = getThreePhaseTemperatureDiff(file, t);

  switch (imageKey) {
    case "visual":
      return [
        {
          label: t("fusionPanel.kpi.detectedDb", {
            defaultValue: "Detected dB",
          }),
          value: displayValue(file?.avg_decibel ?? file?.decibel, " dB"),
        },
        {
          label: t("fileValueTable.ultrasonic.max", { defaultValue: "Max" }),
          value: displayValue(file?.max_decibel, " dB"),
        },
        {
          label: t("fileValueTable.ultrasonic.peakFrequency", {
            defaultValue: "Peak Frequency",
          }),
          value: displayValue(file?.peak_hz, " kHz"),
        },
      ];
    case "graph":
      return [
        {
          label: t("fusionPanel.sections.fftGraph", {
            defaultValue: "FFT Graph",
          }),
          value: t("fusionPanel.sections.fftRange", {
            defaultValue: "0-48 kHz",
          }),
        },
        {
          label: t("fileValueTable.ultrasonic.max", { defaultValue: "Max" }),
          value: displayValue(file?.max_decibel, " dB"),
        },
        {
          label: t("fileValueTable.ultrasonic.peakFrequency", {
            defaultValue: "Peak Frequency",
          }),
          value: displayValue(file?.peak_hz, " kHz"),
        },
      ];
    case "spectrogram":
      return [
        {
          label: t("fusionPanel.sections.frequencyDistribution", {
            defaultValue: "Frequency Distribution",
          }),
          value: t("fusionPanel.sections.fftRange", {
            defaultValue: "0-48 kHz",
          }),
        },
        {
          label: t("fileValueTable.ultrasonic.detected", {
            defaultValue: "Detected",
          }),
          value: displayValue(file?.avg_decibel ?? file?.decibel, " dB"),
        },
        {
          label: t("fileValueTable.ultrasonic.peakFrequency", {
            defaultValue: "Peak Frequency",
          }),
          value: displayValue(file?.peak_hz, " kHz"),
        },
      ];
    case "thermal":
      return [
        {
          label: t("fileValueTable.thermal.detected", {
            defaultValue: "Detected",
          }),
          value: displayValue(file?.thermal_center, " °C"),
        },
        {
          label: t("fileValueTable.thermal.max", { defaultValue: "Max" }),
          value: displayValue(file?.thermal_max, " °C"),
        },
        {
          label: t("fileValueTable.thermal.min", { defaultValue: "Min" }),
          value: displayValue(file?.thermal_min, " °C"),
        },
        {
          label: t("fileValueTable.thermal.avg", { defaultValue: "Average" }),
          value: displayValue(file?.thermal_avg, " °C"),
        },
      ];
    case "triplespot":
      if (thermalClickPointValues.length < 3) {
        return [
          {
            label: t("fusionPanel.metrics.threePhaseAnalysis", {
              defaultValue: "Three-Phase Analysis",
            }),
            value: t("fusionPanel.status.threePhaseRequired", {
              defaultValue: "3상 비교 분석이 필요합니다.",
            }),
          },
        ];
      }

      return [
        ...thermalClickPointValues.map((value, index) => ({
          label: `L${index + 1}`,
          value,
        })),
        {
          label: t("fusionPanel.metrics.threePhaseTemperatureDiff", {
            defaultValue: "Three-Phase Temperature Diff",
          }),
          value: threePhaseTemperatureDiff,
        },
      ];
    case "uv":
      return [
        {
          label: t("fileValueTable.ultraviolet.detected", {
            defaultValue: "Detected",
          }),
          value: displayValue(file?.uv_detected),
        },
        {
          label: t("fileValueTable.ultraviolet.particleCount", {
            defaultValue: "Particle Count",
          }),
          value: displayValue(file?.particle_count),
        },
        {
          label: t("fileValueTable.ultraviolet.maxParticleArea", {
            defaultValue: "Max Particle Area",
          }),
          value: displayValue(file?.max_particle_area, " px²"),
        },
      ];
    default:
      return [];
  }
}

const DOWNLOAD_FILE_KEY_BY_ITEM_KEY = {
  image_visual: "visual",
  markup_image_graph: "fftgraph",
  image_spectrogram: "spectrogram",
  markup_image_thermal: "thermal",
  markup_image_triplespot: "triplespot",
  image_uv: "uv",
};

export function getDownloadableImageItems(file, t) {
  return [
    {
      key: "image_visual",
      label: t("fusionPanel.downloadOptions.imageVisual", {
        defaultValue: "초음파 이미지",
      }),
      analysisKey: "visual",
      src: file?.image_visual || file?.markup_image_visual,
    },
    {
      key: "markup_image_graph",
      label: t("fusionPanel.downloadOptions.markupImageGraph", {
        defaultValue: "FFT 그래프 이미지",
      }),
      analysisKey: "graph",
      src: file?.markup_image_graph,
    },
    {
      key: "image_spectrogram",
      label: t("fusionPanel.downloadOptions.imageSpectrogram", {
        defaultValue: "스펙트로그램 이미지",
      }),
      analysisKey: "spectrogram",
      src: file?.image_spectrogram,
    },
    {
      key: "markup_image_thermal",
      label: t("fusionPanel.downloadOptions.markupImageThermal", {
        defaultValue: "열화상 이미지",
      }),
      analysisKey: "thermal",
      src: file?.markup_image_thermal || file?.image_thermal,
    },
    {
      key: "markup_image_triplespot",
      label: t("fusionPanel.downloadOptions.markupImageTriplespot", {
        defaultValue: "열화상 3점 비교 분석 이미지",
      }),
      analysisKey: "triplespot",
      src: file?.markup_image_triplespot,
    },
    {
      key: "image_uv",
      label: t("fusionPanel.downloadOptions.imageUv", {
        defaultValue: "자외선 이미지",
      }),
      analysisKey: "uv",
      src: file?.image_uv || file?.markup_image_uv,
    },
  ].map((item) => ({
    ...item,
    downloadFileKey: DOWNLOAD_FILE_KEY_BY_ITEM_KEY[item.key] ?? item.key,
    analysisRows: getImageAnalysisRows(item.analysisKey, file, t),
  }));
}

export function inferFileExtension(source) {
  if (!hasValue(source)) return "png";

  if (source.startsWith("data:")) {
    const mimeMatch = source.match(/^data:([^;]+);/i);
    if (!mimeMatch?.[1]) return "png";
    const mimeType = mimeMatch[1].toLowerCase();
    if (mimeType.includes("jpeg")) return "jpg";
    if (mimeType.includes("png")) return "png";
    if (mimeType.includes("webp")) return "webp";
    if (mimeType.includes("gif")) return "gif";
    return "png";
  }

  const pathWithoutQuery = source.split("?")[0] || "";
  const extMatch = pathWithoutQuery.match(/\.([a-z0-9]+)$/i);
  if (!extMatch?.[1]) return "png";
  return extMatch[1].toLowerCase();
}

export function sanitizeFileName(value) {
  return String(value ?? "")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");
}

export async function downloadImageFromSource(source, filename) {
  if (!hasValue(source)) return false;

  try {
    const response = await fetch(source);
    if (!response.ok) return false;

    const blob = await response.blob();
    if (!blob?.size) return false;

    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    return false;
  }
}

function SummaryCard({ label, value, accent }) {
  return (
    <article className={`fusionSummaryCard ${accent ? `tone-${accent}` : ""}`}>
      <span>{label}</span>
      <strong>{displayValue(value)}</strong>
    </article>
  );
}

function FusionImageViewport({ image, t, className = "", onOpenDetail }) {
  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onOpenDetail?.(image);
  };

  return (
    <div
      className={`fusionImageScroll fusionImageInteractive ${className}`.trim()}
      role="button"
      tabIndex={0}
      onClick={() => onOpenDetail?.(image)}
      onKeyDown={handleKeyDown}
      aria-label={t("fusionPanel.actions.openImageDetail", {
        defaultValue: "이미지 상세 보기",
      })}
    >
      <img src={image.src} alt={image.label} />
    </div>
  );
}

function ImageTile({ image, t, onOpenDetail }) {
  if (!hasValue(image.src)) {
    return (
      <article className="fusionImageTile is-empty">
        <h4>{image.label}</h4>
        <div className="fusionImageEmpty">
          {t("fusionPanel.status.noImage", { defaultValue: "이미지 없음" })}
        </div>
      </article>
    );
  }

  return (
    <article className="fusionImageTile">
      <h4>{image.label}</h4>
      <FusionImageViewport image={image} t={t} onOpenDetail={onOpenDetail} />
    </article>
  );
}

function FusionImageDetailModal({
  image,
  analysisRows,
  fftGraphData,
  t,
  onClose,
  onOpenDownload,
}) {
  if (!image) return null;

  const isFftGraphDetail = image.analysisKey === "graph";

  return (
    <div className="fusionModalBackdrop" role="presentation" onClick={onClose}>
      <div
        className="fusionImageDetailModal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="fusionModalCloseButton"
          onClick={onClose}
          aria-label={t("fusionPanel.actions.closeModal", {
            defaultValue: "닫기",
          })}
        >
          <X size={18} aria-hidden="true" />
        </button>
        <div
          className={`fusionImageDetailMedia ${
            isFftGraphDetail ? "is-fft-graph" : ""
          }`.trim()}
        >
          {isFftGraphDetail ? (
            <div className="fusionFftDetailChart" aria-label={image.label}>
              {fftGraphData ? (
                <FftBarGraph data={fftGraphData} isCapture />
              ) : (
                <div className="fusionImageEmpty">
                  {t("fusionPanel.status.noFftData", {
                    defaultValue: "FFT 데이터 없음",
                  })}
                </div>
              )}
            </div>
          ) : (
            <img src={image.src} alt={image.label} />
          )}
        </div>
        <aside className="fusionImageDetailInfo">
          <h5>{image.label}</h5>
          <p>
            {t("fusionPanel.sections.imageSummary", {
              defaultValue: "선택한 이미지의 핵심 분석 값",
            })}
          </p>
          <dl>
            {(analysisRows || []).map((row) => (
              <div key={`${image.key}-${row.label}`}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
          <button
            type="button"
            className="fusionPrimaryButton"
            onClick={() => onOpenDownload?.(image)}
          >
            <Download size={16} aria-hidden="true" />
            {t("fusionPanel.actions.downloadImages", {
              defaultValue: "이미지 다운로드",
            })}
          </button>
        </aside>
      </div>
    </div>
  );
}

export function FusionDownloadModal({
  open,
  items,
  selectedMap,
  t,
  onClose,
  onToggle,
  onSelectAll,
  onClearAll,
  onDownload,
}) {
  if (!open) return null;

  return (
    <div className="fusionModalBackdrop" role="presentation" onClick={onClose}>
      <div
        className="fusionDownloadModal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="fusionDownloadModalHeader">
          <h5>
            {t("fusionPanel.actions.downloadSelection", {
              defaultValue: "다운로드할 이미지 선택",
            })}
          </h5>
          <button
            type="button"
            className="fusionModalCloseButton"
            onClick={onClose}
            aria-label={t("fusionPanel.actions.closeModal", {
              defaultValue: "닫기",
            })}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="fusionDownloadQuickActions">
          <button type="button" onClick={onSelectAll}>
            {t("fusionPanel.actions.selectAll", { defaultValue: "전체 선택" })}
          </button>
          <button type="button" onClick={onClearAll}>
            {t("fusionPanel.actions.clearAll", { defaultValue: "선택 해제" })}
          </button>
        </div>

        <ul className="fusionDownloadList">
          {items.map((item) => {
            const isAvailable = hasValue(item.src);
            const isSelected = Boolean(selectedMap[item.key]);
            const handleToggleByKey = (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onToggle(item.key);
            };

            return (
              <li
                key={item.key}
                className={isAvailable ? "" : "is-missing"}
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => onToggle(item.key)}
                onKeyDown={handleToggleByKey}
              >
                <label onClick={(event) => event.preventDefault()}>
                  <input type="checkbox" checked={isSelected} readOnly />
                  <span>{item.label}</span>
                </label>
                <small>
                  {isAvailable
                    ? t("fusionPanel.status.imageReady", {
                        defaultValue: "사용 가능",
                      })
                    : t("fusionPanel.status.imageMissing", {
                        defaultValue: "데이터 없음",
                      })}
                </small>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="fusionPrimaryButton fusionDownloadConfirm"
          onClick={onDownload}
        >
          <Download size={16} aria-hidden="true" />
          {t("fusionPanel.actions.downloadSelected", {
            defaultValue: "선택 이미지 다운로드",
          })}
        </button>
      </div>
    </div>
  );
}

function FusionTriplespotGuideModal({ open, t, onClose, onMove }) {
  if (!open) return null;

  return (
    <div className="fusionModalBackdrop" role="presentation" onClick={onClose}>
      <div
        className="fusionNoticeModal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <h5>
          {t("fusionPanel.status.threePhaseRequired", {
            defaultValue: "3상 비교 분석이 필요합니다.",
          })}
        </h5>
        <p>
          {t("fusionPanel.status.moveThreePhaseGuide", {
            defaultValue:
              "요청한 이미지가 아직 없습니다. 3상 비교 분석 패널에서 생성 후 다시 시도해 주세요.",
          })}
        </p>
        <div className="fusionNoticeActions">
          <button type="button" onClick={onClose}>
            {t("fusionPanel.actions.cancel", { defaultValue: "취소" })}
          </button>
          <button
            type="button"
            className="fusionPrimaryButton"
            onClick={onMove}
          >
            {t("fusionPanel.actions.moveThreePhase", {
              defaultValue: "3상 비교 분석으로 이동",
            })}
          </button>
        </div>
      </div>
    </div>
  );
}

function MetricGroupTable({ group, thermalClickPointValues, t }) {
  const isThermal = group.key === "thermal";
  const hasThermalPointValues = thermalClickPointValues.length === 3;

  if (isThermal) {
    const thermalPointRows = hasThermalPointValues
      ? [
          ...thermalClickPointValues.map((value, index) => ({
            label: `L${index + 1}`,
            value,
            isPoint: true,
          })),
          {
            label: t("fusionPanel.metrics.threePhaseTemperatureDiff", {
              defaultValue: "3상 비교 온도차",
            }),
            value: `${(
              Math.max(...thermalClickPointValues.map(parseNumber)) -
              Math.min(...thermalClickPointValues.map(parseNumber))
            ).toFixed(1)} °C`,
            isPoint: true,
          },
        ]
      : [
          {
            label: t("fusionPanel.metrics.threePhaseAnalysis", {
              defaultValue: "3상 비교 분석",
            }),
            value: t("fusionPanel.status.notStarted", {
              defaultValue: "미진행",
            }),
            isPoint: true,
          },
        ];
    const rows = group.rows.flatMap((row, index) =>
      index === 3 ? [row, ...thermalPointRows] : [row],
    );

    return (
      <table className="fusionMetricsTable fusionThermalMetricsTable">
        <tbody>
          {rows.map((row) => (
            <tr
              className={row.isPoint ? "fusionThermalPointRow" : ""}
              key={`${row.group ?? "thermal-point"}-${row.label}`}
            >
              <td>{row.label}</td>
              <td>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className="fusionMetricsTable">
      <tbody>
        {group.rows.map((row) => (
          <tr key={`${row.group}-${row.label}`}>
            <td>{row.label}</td>
            <td>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function FusionPanel({ file, t, onMoveToThreePhase }) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [detailImage, setDetailImage] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadSelectionMap, setDownloadSelectionMap] = useState({});
  const [showThreePhaseGuide, setShowThreePhaseGuide] = useState(false);
  const showAlert = useAppStore((s) => s.showAlert);
  const { i18n } = useTranslation();
  const fusionValue = file?.fusion_result_code ?? file?.fusion_result;
  const fusionLabel = getFusionResultLabel(fusionValue, i18n, {
    fallback: displayValue(file?.fusion_result),
  });
  const fusionTone = getFusionResultTone(fusionValue) || "unknown";
  const metricRows = getMetricRows(file, t, fusionLabel);
  const metricGroups = getMetricGroups(metricRows);
  const slideImages = getSlideImages(file, t);
  const fftGraphImage = getFftGraphImage(file, t);
  const spectrogramImage = getSpectrogramImage(file, t);
  const downloadImageItems = getDownloadableImageItems(file, t);
  const canOpenFftGraphDetail =
    hasValue(fftGraphImage.src) || Boolean(file?.fft_graph_array);
  const normalizedActiveImageIndex = slideImages.length
    ? activeImageIndex % slideImages.length
    : 0;
  const activeImage = slideImages[normalizedActiveImageIndex] ?? slideImages[0];
  const threePhaseTemperatureDiff = getThreePhaseTemperatureDiff(file, t);
  const thermalClickPointValues = getThermalClickPointValues(file);

  const moveSlide = (direction) => {
    setActiveImageIndex((current) => {
      const total = slideImages.length;
      if (!total) return 0;
      return (current + direction + total) % total;
    });
  };

  const openImageDetail = (image) => {
    const canOpenGraphDetail =
      image?.analysisKey === "graph" && Boolean(file?.fft_graph_array);
    if (!hasValue(image?.src) && !canOpenGraphDetail) return;
    setDetailImage(image);
  };

  const openFftGraphDetailByKey = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    openImageDetail(fftGraphImage);
  };

  const openDownloadModal = (targetImage = null) => {
    const preselectedKey = targetImage?.downloadKey;
    setDownloadSelectionMap(() => {
      const next = {};
      downloadImageItems.forEach((item) => {
        next[item.key] = preselectedKey ? item.key === preselectedKey : false;
      });
      return next;
    });
    setIsDownloadModalOpen(true);
  };

  const toggleDownloadSelection = (key) => {
    setDownloadSelectionMap((previous) => ({
      ...previous,
      [key]: !previous[key],
    }));
  };

  const selectAllDownloads = () => {
    setDownloadSelectionMap(
      downloadImageItems.reduce((acc, item) => {
        acc[item.key] = true;
        return acc;
      }, {}),
    );
  };

  const clearAllDownloads = () => {
    setDownloadSelectionMap(
      downloadImageItems.reduce((acc, item) => {
        acc[item.key] = false;
        return acc;
      }, {}),
    );
  };

  const handleDownloadSelectedImages = async () => {
    const selectedItems = downloadImageItems.filter(
      (item) => downloadSelectionMap[item.key],
    );

    if (!selectedItems.length) {
      showAlert({
        success: false,
        autoClose: true,
        text: t("fusionPanel.status.selectImageRequired", {
          defaultValue: "다운로드할 이미지를 선택해 주세요.",
        }),
      });
      return;
    }

    const baseName = sanitizeFileName(
      file?.clean_file_name || file?.file_name || file?.upload_id || "fusion",
    );
    let successCount = 0;
    let failCount = 0;
    let requestedTriplespotWithoutImage = false;

    for (const item of selectedItems) {
      if (!hasValue(item.src)) {
        if (item.key === "markup_image_triplespot") {
          requestedTriplespotWithoutImage = true;
        } else {
          failCount += 1;
        }
        continue;
      }

      const extension = inferFileExtension(item.src);
      const fileName = `${baseName}_${item.downloadFileKey ?? item.key}.${extension}`;
      const success = await downloadImageFromSource(item.src, fileName);
      if (success) successCount += 1;
      else failCount += 1;
    }

    if (successCount > 0) {
      showAlert({
        success: true,
        autoClose: true,
        text: t("fusionPanel.status.downloadSuccess", {
          defaultValue: "{{count}}개 이미지를 다운로드했습니다.",
          count: successCount,
        }),
      });
    }

    if (failCount > 0) {
      showAlert({
        success: false,
        autoClose: true,
        text: t("fusionPanel.status.downloadFailed", {
          defaultValue: "{{count}}개 이미지 다운로드에 실패했습니다.",
          count: failCount,
        }),
      });
    }

    setIsDownloadModalOpen(false);

    if (requestedTriplespotWithoutImage) {
      setShowThreePhaseGuide(true);
    }
  };

  return (
    <div className="fusionPanel fileTabSection">
      <div className="fusionHero fileTabHeader">
        <div className="fileTabHeader__copy">
          <h3>
            {t("fusionPanel.hero.title", { defaultValue: "융복합 분석 요약" })}
          </h3>
          <p>
            {t("fusionPanel.hero.description", {
              defaultValue: "초음파, 열화상, 자외선 결과 기반 통합 진단입니다.",
            })}
          </p>
        </div>
      </div>

      <div className="fusionSummaryGrid">
        <SummaryCard
          label={t("fusionPanel.kpi.detectedDb", { defaultValue: "검출 dB" })}
          value={displayValue(file?.avg_decibel ?? file?.decibel, " dB")}
          accent="ultrasonic"
        />
        <SummaryCard
          label={t("fusionPanel.kpi.detectedTemperature", {
            defaultValue: "검출 온도",
          })}
          value={displayValue(file?.thermal_center, " °C")}
          accent="thermal"
        />
        <SummaryCard
          label={t("fusionPanel.kpi.ultraviolet", { defaultValue: "자외선" })}
          value={displayValue(file?.uv_detected)}
          accent="uv"
        />
        <SummaryCard
          label={t("fusionPanel.kpi.threePhaseTemperatureDiff", {
            defaultValue: "3상 비교 온도차",
          })}
          value={threePhaseTemperatureDiff}
          accent="image"
        />
        <SummaryCard
          label={t("fusionPanel.kpi.distance", { defaultValue: "측정 거리" })}
          value={displayValue(file?.distance, " m")}
          accent="metric"
        />
        <SummaryCard
          label={t("fusionPanel.status.result", { defaultValue: "결과" })}
          value={fusionLabel}
          accent={fusionTone}
        />
      </div>

      <div className="fusionDashboardGrid">
        <div className="fusionDashboardColumn fusionDashboardPrimaryColumn">
          <section className="fusionDashboardItem fusionMediaPanel">
            <div className="fusionSectionTitle">
              <h4>
                {t("fusionPanel.sections.diagnosticImages", {
                  defaultValue: "진단 이미지",
                })}
              </h4>
              <span>
                {t("fusionPanel.sections.referenceImage", {
                  defaultValue: "기준 이미지",
                })}
              </span>
            </div>
            <div className="fusionImageSlider">
              <div className="fusionSlideStage">
                <ImageTile
                  image={activeImage}
                  t={t}
                  onOpenDetail={openImageDetail}
                />
                <button
                  type="button"
                  className="fusionSlideArrow prev"
                  onClick={() => moveSlide(-1)}
                  aria-label={t("fusionPanel.actions.previousImage", {
                    defaultValue: "이전 이미지",
                  })}
                >
                  <span aria-hidden="true">‹</span>
                </button>
                <button
                  type="button"
                  className="fusionSlideArrow next"
                  onClick={() => moveSlide(1)}
                  aria-label={t("fusionPanel.actions.nextImage", {
                    defaultValue: "다음 이미지",
                  })}
                >
                  <span aria-hidden="true">›</span>
                </button>
                <div className="fusionSlideControls">
                  <div className="fusionSlideDots">
                    {slideImages.map((image, index) => (
                      <button
                        type="button"
                        key={image.key}
                        className={
                          index === normalizedActiveImageIndex ? "active" : ""
                        }
                        onClick={() => setActiveImageIndex(index)}
                        aria-label={image.label}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="fusionDashboardItem fusionSignalPanel">
            <div className="fusionSectionTitle">
              <h4>
                {t("fusionPanel.sections.signalAnalysis", {
                  defaultValue: "신호 분석",
                })}
              </h4>
              <span>
                {t("fusionPanel.sections.signalTargets", {
                  defaultValue: "FFT / 스펙트로그램",
                })}
              </span>
            </div>
            <div className="fusionSignalStack">
              <div className="fusionAnalysisBlock">
                <div className="fusionBlockTitle">
                  <h5>
                    {t("fusionPanel.sections.fftGraph", {
                      defaultValue: "FFT 그래프",
                    })}
                  </h5>
                  <span>
                    {t("fusionPanel.sections.fftRange", {
                      defaultValue: "0-48 kHz",
                    })}
                  </span>
                </div>
                <div
                  className={`fusionFftBox ${
                    canOpenFftGraphDetail ? "fusionFftInteractive" : ""
                  }`.trim()}
                  role={canOpenFftGraphDetail ? "button" : undefined}
                  tabIndex={canOpenFftGraphDetail ? 0 : undefined}
                  onClick={
                    canOpenFftGraphDetail
                      ? () => openImageDetail(fftGraphImage)
                      : undefined
                  }
                  onKeyDown={
                    canOpenFftGraphDetail ? openFftGraphDetailByKey : undefined
                  }
                  aria-label={
                    canOpenFftGraphDetail
                      ? t("fusionPanel.actions.openImageDetail", {
                          defaultValue: "이미지 상세 보기",
                        })
                      : undefined
                  }
                >
                  {file?.fft_graph_array ? (
                    <FftBarGraph data={file.fft_graph_array} />
                  ) : (
                    <div className="fusionImageEmpty">
                      {t("fusionPanel.status.noFftData", {
                        defaultValue: "FFT 데이터 없음",
                      })}
                    </div>
                  )}
                </div>
              </div>

              {hasValue(spectrogramImage.src) ? (
                <div className="fusionAnalysisBlock">
                  <div className="fusionBlockTitle">
                    <h5>{spectrogramImage.label}</h5>
                    <span>
                      {t("fusionPanel.sections.frequencyDistribution", {
                        defaultValue: "주파수 분포",
                      })}
                    </span>
                  </div>
                  <FusionImageViewport
                    image={spectrogramImage}
                    t={t}
                    className="fusionSpectrogramBox"
                    onOpenDetail={openImageDetail}
                  />
                </div>
              ) : null}
            </div>
          </section>
        </div>

        <div className="fusionDashboardColumn fusionDashboardSideColumn">
          <div className="fusionMetricsPanel">
            <div className="fusionSectionTitle">
              <h4>
                {t("fusionPanel.sections.metrics", {
                  defaultValue: "종합 지표",
                })}
              </h4>
              <span>
                {t("fusionPanel.sections.measurements", {
                  defaultValue: "측정값",
                })}
              </span>
            </div>
            <div className="fusionMetricGroupGrid">
              {metricGroups.map((group) => (
                <article
                  className={`fusionMetricGroup ${
                    group.key === "thermal" ? "is-thermal" : ""
                  }`}
                  key={group.title}
                >
                  <h5>{group.title}</h5>
                  <MetricGroupTable
                    group={group}
                    thermalClickPointValues={thermalClickPointValues}
                    t={t}
                  />
                </article>
              ))}
            </div>
          </div>

          {hasValue(file?.inspection_result) ? (
            <div className="fusionOpinionPanel">
              <div className="fusionSectionTitle">
                <h4>
                  {t("fusionPanel.sections.opinion", {
                    defaultValue: "종합 의견",
                  })}
                </h4>
                <span>
                  {t("fusionPanel.sections.inspectionResult", {
                    defaultValue: "검사 결과",
                  })}
                </span>
              </div>
              <p>{file.inspection_result}</p>
            </div>
          ) : null}
        </div>
      </div>

      <FusionImageDetailModal
        image={detailImage}
        analysisRows={getImageAnalysisRows(detailImage?.analysisKey, file, t)}
        fftGraphData={file?.fft_graph_array}
        t={t}
        onClose={() => setDetailImage(null)}
        onOpenDownload={openDownloadModal}
      />

      <FusionDownloadModal
        open={isDownloadModalOpen}
        items={downloadImageItems}
        selectedMap={downloadSelectionMap}
        t={t}
        onClose={() => setIsDownloadModalOpen(false)}
        onToggle={toggleDownloadSelection}
        onSelectAll={selectAllDownloads}
        onClearAll={clearAllDownloads}
        onDownload={handleDownloadSelectedImages}
      />

      <FusionTriplespotGuideModal
        open={showThreePhaseGuide}
        t={t}
        onClose={() => setShowThreePhaseGuide(false)}
        onMove={() => {
          setShowThreePhaseGuide(false);
          if (typeof onMoveToThreePhase === "function") {
            onMoveToThreePhase();
          }
        }}
      />
    </div>
  );
}
